import { createFileRoute } from '@tanstack/react-router'

import type { CompanyWithDetails } from '@/api/admin-companies-types'
import { useUpdateCompany } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/_general/companies/$companyUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Companies.EditTitle()
	}
})

function RouteComponent() {
	const { companyUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getCompany({ path: { companyUuid } })}>
			{data => <EditCompanyForm company={data} />}
		</RouteModal>
	)
}

function EditCompanyForm({ company }: { company: CompanyWithDetails }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { companyUuid } = Route.useParams()

	const updateCompany = useUpdateCompany()

	const form = useAppForm({
		defaultValues: {
			name: company.name || '',
			email: company.email || '',
			phone: company.phone || '',
			orgNumber: company.orgNumber || '',
			externalId: company.externalId || ''
		},
		onSubmit: async ({ value }) => {
			await updateCompany({
				path: { companyUuid },
				body: {
					name: value.name,
					email: value.email || null,
					phone: value.phone || null,
					orgNumber: value.orgNumber || null,
					externalId: value.externalId || undefined
				}
			})
			toast.success(L.Companies.UpdateSuccess())
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Companies.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Companies.Edit.NamePlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='email'
							children={field => (
								<field.TextField
									label={L.Fields.Email()}
									placeholder={L.Companies.Edit.EmailPlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='phone'
							children={field => (
								<field.TextField
									label={L.Fields.Phone()}
									placeholder={L.Companies.Edit.PhonePlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='orgNumber'
							children={field => (
								<field.TextField
									label={L.Fields.OrgNumber()}
									placeholder={L.Companies.Edit.OrgNumberPlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='externalId'
							children={field => (
								<field.TextField
									label={L.Fields.ExternalId()}
									placeholder={L.Companies.Edit.ExternalIdPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
